import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import recordApi from '../../apis/RecordApi';
import CustomContext from '../../context/CustomContext';
import { Season, Sport, User } from '../../model/Entities';
import playerCore4ScoreService from '../../services/core4/PlayerCore4Service';
import styles from './PlayerCore4ScoreComponent.module.scss';
import { Tooltip } from 'antd';
import resultApi from '../../apis/ResultApi';
import { ReactComponent as AccuracySvg } from '../../resources/images/ico-core4Accuracy.svg';
import { ReactComponent as AgilitySvg } from '../../resources/images/ico-core4Agility.svg';
import { ReactComponent as ArmVelocitySvg } from '../../resources/images/ico-core4ArmVelocity.svg';
import { ReactComponent as HittingSvg } from '../../resources/images/ico-core4Hitting.svg';
import { ReactComponent as SpeedSvg } from '../../resources/images/ico-core4Speed.svg';
import { ReactComponent as SpeedAgilitySvg } from '../../resources/images/ico-core4SpeedAgility.svg';
import { ReactComponent as StrengthSvg } from '../../resources/images/ico-core4Strength.svg';

/**
 * This component display the Top100 Sports Core4 score of a player.
 */
const PlayerCore4ScoreComponent: React.FC<Props> = (props) => {
    const { user, sport, season } = props;
    const context = useContext(CustomContext);
    const { auth } = context;

    /**^* METHODS */

    /*** HOOKS */

    const [score, setScore] = useState<number>();
    const [tier, setTier] = useState<number>(1);
    const [processed, setProcessed] = useState<boolean>(false);
    const [loading, setLoading] = useState<'loading' | undefined>('loading');
    const [categoryScores, setCategoryScores] = useState<Record<string, number | undefined>>({});

    // Category codes only depend on the sport, so they are available synchronously and let the
    // panel render its rows (labels + colors) immediately, before the async scores are computed.
    const categoryCodes = useMemo(() => (sport ? playerCore4ScoreService.getCategories(sport) : []), [sport]);

    useEffect(() => {
        const init = async () => {
            try {
                if (sport && sport.id && season && user && user.id && user.profile.birthday && user.profile.gender) {
                    setLoading('loading');

                    const records = await recordApi.list(user.id, sport.id!, season.id);
                    const results = await resultApi.list(user.id, sport.id!, season.id);
                    const breakdown = playerCore4ScoreService.getScoreBreakdown(
                        sport,
                        season,
                        user.profile.birthday,
                        user.profile.gender,
                        records,
                        results,
                    );
                    const tier = playerCore4ScoreService.getTier(breakdown.score);
                    const categoryScoresByCode: Record<string, number | undefined> = {};
                    breakdown.categoryScores.forEach((categoryScore) => {
                        categoryScoresByCode[categoryScore.code] = categoryScore.score;
                    });

                    setProcessed(true);
                    setScore(breakdown.score);
                    setTier(tier);
                    setCategoryScores(categoryScoresByCode);
                } else {
                    setScore(undefined);
                    setTier(1);
                    setCategoryScores({});
                }
            } catch (error) {
            } finally {
                setLoading(undefined);
            }
        };

        init();
    }, [auth, sport, season, user]);

    /*** COMPONENTS ***/

    return (
        <div className={styles.playerScore}>
            <a target="_blank" rel="noreferrer" href="https://top100sports.com/about/core4">
                <div className={styles.box}>
                    <img src={`${process.env.PUBLIC_URL}/img/core4/logo.png`} alt="Core4" className={styles.logo} />
                    <div className={styles.score}>
                        <span className={styles.result}>
                            <ScoreComponent score={score} processed={processed} loading={loading} />
                        </span>
                        <span className={styles.tierName}>
                            <TierNameComponent tier={tier} processed={processed} />
                        </span>
                    </div>
                </div>
                <TierComponent tier={tier} processed={processed} />
            </a>
            <CategoriesComponent categoryCodes={categoryCodes} categoryScores={categoryScores} loading={loading} />
        </div>
    );
};
export default PlayerCore4ScoreComponent;

interface Props {
    user: User | undefined;
    sport: Sport | undefined;
    season: Season | undefined;
}

const ScoreComponent: React.FC<ScoreComponentProps> = (props) => {
    const { score, loading } = props;

    if (loading === 'loading') {
        return <>&nbsp;</>;
    } else if (score !== undefined) {
        const truncatedScore = Math.floor(score);
        return <FormattedNumber value={truncatedScore} minimumFractionDigits={0} maximumFractionDigits={0} />;
    } else {
        return <FormattedMessage id="--" />;
    }
};
interface ScoreComponentProps {
    score: number | undefined;
    processed: boolean;
    loading?: 'loading';
}

const TierNameComponent: React.FC<TierNameComponentProps> = (props) => {
    const { tier, processed } = props;

    if (processed) {
        return <FormattedMessage id={`player.core4.tier.${tier}`} />;
    } else {
        return <>&nbsp;</>;
    }
};
interface TierNameComponentProps {
    tier: number | undefined;
    processed: boolean;
}

const TierComponent: React.FC<TierComponentProps> = (props) => {
    const { tier, processed } = props;

    if (processed) {
        return (
            <Tooltip title={<FormattedMessage id={`player.core4.tier.${tier}.tooltip`} />} placement="left">
                <img src={`${process.env.PUBLIC_URL}/img/core4/t${tier}.png`} alt="Score" className={styles.tier} />
            </Tooltip>
        );
    } else {
        return <>&nbsp;</>;
    }
};
interface TierComponentProps {
    tier: number | undefined;
    processed: boolean;
}

const CATEGORY_PALETTE = ['blue', 'red', 'orange', 'green'] as const;

const CATEGORY_ICONS: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
    ACCURACY: AccuracySvg,
    AGILITY: AgilitySvg,
    ARMVELOCITY: ArmVelocitySvg,
    HITTING: HittingSvg,
    SPEED: SpeedSvg,
    SPEEDAGILITY: SpeedAgilitySvg,
    STRENGTH: StrengthSvg,
};

const CategoriesComponent: React.FC<CategoriesComponentProps> = (props) => {
    const { categoryCodes, categoryScores, loading } = props;

    if (categoryCodes.length === 0) {
        return null;
    }

    return (
        <div className={styles.categories}>
            <div className={styles.categoriesHeader}>
                <FormattedMessage id="player.core4.categories.title" />
            </div>
            <div className={styles.categoryList}>
                {categoryCodes.map((code, index) => (
                    <CategoryRowComponent
                        key={code}
                        code={code}
                        score={categoryScores[code]}
                        palette={CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]}
                        loading={loading}
                    />
                ))}
            </div>
        </div>
    );
};
interface CategoriesComponentProps {
    categoryCodes: string[];
    categoryScores: Record<string, number | undefined>;
    loading?: 'loading';
}

const CategoryRowComponent: React.FC<CategoryRowComponentProps> = (props) => {
    const { code, score, palette, loading } = props;
    const Icon = CATEGORY_ICONS[code];

    return (
        <div className={`${styles.categoryRow} ${styles[palette]}`}>
            <span className={styles.categoryLabelGroup}>
                <span className={styles.categoryDot}>
                    {Icon && <Icon className={styles.categoryIcon} focusable="false" aria-hidden="true" />}
                </span>
                <span className={styles.categoryLabel}>
                    <FormattedMessage id={`player.core4.categories.${code}`} />
                </span>
            </span>
            <span className={styles.categoryScore}>
                <span className={styles.categoryScoreShape} />
                <span className={styles.categoryScoreValue}>
                    <CategoryScoreValue score={score} loading={loading} />
                </span>
            </span>
        </div>
    );
};
interface CategoryRowComponentProps {
    code: string;
    score: number | undefined;
    palette: typeof CATEGORY_PALETTE[number];
    loading?: 'loading';
}

const CategoryScoreValue: React.FC<CategoryScoreValueProps> = (props) => {
    const { score, loading } = props;

    if (loading === 'loading') {
        return <>&nbsp;</>;
    }
    if (score === undefined) {
        return <FormattedMessage id="--" />;
    }

    // Render each digit in an equal-width cell so scores line up uniformly
    // (Poppins ships proportional figures, so tabular-nums has no effect here).
    return (
        <>
            {String(Math.floor(score))
                .split('')
                .map((digit, index) => (
                    <span key={index} className={styles.categoryScoreDigit}>
                        {digit}
                    </span>
                ))}
        </>
    );
};
interface CategoryScoreValueProps {
    score: number | undefined;
    loading?: 'loading';
}
